import { useIsScribeEnableRtlEnabled } from '@/app-shell/flags/shell-flags';
import {
  mapLocaleAliasToSupportedLocale,
  type MapLocaleToSupportedOptions,
} from '@/utils/i18n/supportedLocales';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { type LanguageCode } from '@heidi/schema/ml-scribe';
import { type UserResponse } from '@heidi/workspace/contracts';
import { GET_USER, useQueryUserForContext } from './query/useQueryUser';

/**
 * Returns the current user's display language as a supported app locale.
 *
 * Backend `LanguageCode` values can be aliases or locale variants such as
 * `fr`, `fr-CA`, or `pt-BR`. This hook normalizes those values to the
 * canonical `SupportedLocale` used by the frontend, for example `fr-FR`
 * or `pt-PT`.
 *
 * Returns `undefined` when the user has no display language or when the
 * backend language code does not map to any supported app locale.
 */
export function useQueryUserLanguage() {
  const { data: userLanguage } = useQueryUserForContext(
    (data) => data.language_time_settings?.display_language
  );
  const isRtlLocaleFeatureEnabled = useIsScribeEnableRtlEnabled();
  return mapLocaleAliasToSupportedLocale(userLanguage, {
    isRtlLocaleFeatureEnabled,
  });
}

/**
 * Reads the cached current user's display language and normalizes it to a
 * supported app locale.
 *
 * This is the non-hook equivalent of `useQueryUserLanguage()` for code paths
 * that need synchronous access to the React Query cache.
 */
export const getCurrentUserLanguage = (options?: MapLocaleToSupportedOptions) => {
  const userData = reactQueryClient.getQueryData<UserResponse>([GET_USER]);
  return mapLocaleAliasToSupportedLocale(
    userData?.language_time_settings?.display_language,
    options
  );
};

/**
 * Immediately update the cached display_language so TranslationsProvider
 * doesn't override a pending locale change with a stale value.
 */
export const optimisticallySetUserLanguage = (language: LanguageCode) => {
  reactQueryClient.setQueryData<UserResponse>([GET_USER], (old) => {
    if (!old) return old;
    return {
      ...old,
      language_time_settings: {
        ...old.language_time_settings,
        display_language: language,
      },
    };
  });
};
